import { usePageMeta } from '@/hooks/useSEO';
import {
  HUB_PATH, PageHero, TrustBox, InfoCallout, DataTable, FrameworkCTA, SectionH2, ILink, useBreadcrumbSchema,
} from './_shared';

/* HUB / PILLAR — /saudi-visa-framework-2026/ */
export default function SaudiVisaFrameworkHubPage() {
  usePageMeta(
    'Saudi Arabia Visa & Labor Entry Framework 2026 | Zafco Master Guide',
    "Executive guide to Saudi Arabia's 2026 entry regime, Vision 2030 labor compliance, Qiwa, Mudad, Absher digital stack, and legal entry pathways.",
    { canonical: HUB_PATH },
  );
  useBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Saudi Visa Framework 2026', path: HUB_PATH },
  ]);

  return (
    <article data-testid="saudi-framework-hub-root" className="bg-white">
      <PageHero crumb="Saudi Visa Framework 2026" title="Saudi Arabia Visa Frameworks & Labor Compliance Architecture (2026)" />

      <div className="container mx-auto px-6 max-w-5xl py-12">
        <TrustBox
          author="Zafco Overseas Visa Processing & Regulatory Advisory Division"
          reviewer="Senior Counsel, Middle East Talent Mobility & Consular Affairs"
          standard="Saudi Ministry of Human Resources and Social Development (MHRSD), MOFA, & MEA India (License: B-1873/GUJ/COM/100/5/10332/2023)"
          standardLabel="Regulatory Baseline"
        />

        <section id="executive-thesis">
          <SectionH2 id="executive-thesis-h">1. Executive Thesis: Immigration as Industrial Policy</SectionH2>
          <p className="text-slate-700 leading-relaxed mb-4">
            Saudi Arabia’s 2026 entry regime is a managed system of labor allocation, commercial access, and destination control. As Vision 2030 expands the non-oil economy and Public Investment Fund (PIF) megaprojects accelerate across NEOM, Red Sea Global, AMAALA, and Qiddiya, the state uses entry rules, skills classification, wage monitoring, and residence controls to shape who enters, what tasks they perform, and how they are audited post-arrival.
          </p>
          <p className="text-slate-700 leading-relaxed mb-4">
            For corporate planners, recruitment agencies, and investors, the central operational rule is no longer just <em>"which visa type is available,"</em> but <strong>"which legal activity is permitted, through which portal, under which compliance trail, and with which post-arrival obligations."</strong>
          </p>
          <InfoCallout>
            <strong>The India-Saudi Corridor Alignment:</strong> Saudi Arabia's 18.1M workforce maintains a high expatriate concentration, with over 2.7M Indian nationals defining the operational core across construction, healthcare, energy, IT, and hospitality. Saudi Arabia continues to absorb foreign labor at scale, but now enforces strict digital segmentation of eligibility, occupation codes, and payroll status.
          </InfoCallout>
        </section>

        <section id="digital-governance-stack">
          <SectionH2 id="digital-stack-h">2. The KSA Digital Governance Stack</SectionH2>
          <p className="text-slate-700 leading-relaxed">Employment and mobility in the Kingdom are governed by interconnected government authority platforms:</p>
          <DataTable
            testId="governance-stack-table"
            headers={['Platform Engine', 'Government Authority', 'Primary Operational Control']}
            rows={[
              [<strong>Qiwa</strong>, 'MHRSD', 'Work permit issuance, employment contract validation, candidate transfer logic, and SSCO occupation code classification.'],
              [<strong>Mudad</strong>, 'MHRSD / GOSI', <>Wage Protection Program (WPP) compliance. Enforces mandatory wage file uploads within a strict <strong>30-day window</strong>.</>],
              [<strong>Absher & Muqeem</strong>, 'MOI / Jawazat', 'Resident identity tracking, Iqama card issuance/renewals, exit-reentry permissions, and final exit processing.'],
              [<strong>Enjaz / MOFA</strong>, 'Ministry of Foreign Affairs', 'Consular invitation authorization, electronic visa application processing, and passport stamping clearance.'],
            ]}
          />
        </section>

        <section id="legal-boundaries">
          <SectionH2 id="legal-boundaries-h">3. Critical Legal Distinction: Nitaqat vs. The Expatriate Contract</SectionH2>
          <p className="text-slate-700 leading-relaxed mb-4">A fundamental area of corporate liability is confusing employer Saudization accounting with worker labor rights:</p>
          <ul className="list-disc pl-6 space-y-3 text-slate-700 leading-relaxed">
            <li><strong>Nitaqat System:</strong> An employer-facing localization accounting mechanism. The SAR 4,000 monthly threshold belongs strictly to Nitaqat classification logic for counting a Saudi national as a full employee; <strong>it is not an expatriate minimum wage benchmark</strong> and must never be represented as one.</li>
            <li><strong>Expatriate Employment Contract:</strong> The worker-facing legal instrument registered on Qiwa that governs basic salary, fixed allowances, job duties, notice periods, and statutory labor rights. Foreign workers are governed strictly by their written contract and Mudad payroll records. Estimate statutory severance exposure with our <ILink to="/resources/saudi-end-of-service-gratuity-calculator/" testId="hub-link-gratuity">End-of-Service Gratuity Calculator</ILink>.</li>
          </ul>
        </section>

        <FrameworkCTA />

        <section id="entry-channels">
          <SectionH2 id="entry-channels-h">4. Overview of Key Saudi Entry Channels (2026)</SectionH2>
          <DataTable
            testId="entry-channels-table"
            headers={['Visa Channel', 'Permitted Scope', 'Sponsorship Requirement', 'Detailed Guide']}
            rows={[
              [
                <strong>Work Visa (Employment)</strong>,
                'Long-term paid employment and local workforce deployment.',
                'Tied to a single employer sponsor (Qiwa / Iqama).',
                <ILink to="/services/saudi-work-visa-qvp-attestation/" testId="hub-link-work-visa">View Work Visa & QVP Guide →</ILink>,
              ],
              [
                <strong>Commercial Business Visit</strong>,
                'B2B meetings, contract negotiations, board strategy, technical audits.',
                'Requires MOFA Letter of Invitation from Saudi host entity.',
                <ILink to="/services/saudi-business-visit-visa/" testId="hub-link-business-visa">View Business Visit Guide →</ILink>,
              ],
              [
                <strong>Tourist eVisa / On Arrival</strong>,
                'Leisure travel, cultural events, Umrah (excluding Hajj). Strictly no paid work.',
                'Sponsor-free (Digital authorization).',
                <ILink to="/saudi-arabia/tourist-visa" testId="hub-link-tourist">View Tourist Rules →</ILink>,
              ],
              [
                <strong>Premium Residency</strong>,
                'Sponsor-free long-term residency, business ownership, property rights.',
                'Decoupled from employer sponsorship (PRC approval).',
                <ILink to="/services/saudi-business-visit-visa/#premium-residency" testId="hub-link-premium">View Premium Residency Routes →</ILink>,
              ],
            ]}
          />
        </section>

        <section id="zafco-role">
          <SectionH2 id="zafco-role-h">5. Zafco’s Role in Operational Risk Reduction</SectionH2>
          <p className="text-slate-700 leading-relaxed">
            Zafco operates inside the pre-execution compliance workflow as an employer-auditing and mobilization layer. Before candidates are deployed, Zafco verifies the employer's portal health on Qiwa, payroll compliance on Mudad, and contract legitimacy. While MHRSD remains the statutory enforcer, Zafco serves as the commercial verification anchor that eliminates deployment risk. For role-specific requirements (Engineer, Technician, Nurse, Driver, Labour), see our <ILink to="/saudi-arabia/work-visa" testId="hub-link-old-work">Saudi Work Visa role-wise requirements guide</ILink>.
          </p>
        </section>
      </div>
    </article>
  );
}
